import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {paymentRequestGetAll} from '../../../redux/slices/paymentrequest.slice'
import {
  projectLookup,
  basicLookup,
  allCompanyLookup,
  userProjectLookup,
} from '../../../redux/slices/lookup.slice'

const selectPaymentRequestData = (state) => ({
  paymentRequest: state.paymentrequest.paymentRequest,
  loading: state.paymentrequest.loading,
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  PaymentRequestType: state.lookup.PaymentRequestType,
  PaymentRequestState: state.lookup.PaymentRequestState,
  userProjectLookupList: state.lookup.userProjectLookupList,
})

function List() {
  const intl = useIntl()
  const {paymentRequest, loading} = useSelector(selectPaymentRequestData)
  const {
    projectLookupList,
    companyLookup,
    PaymentRequestType,
    PaymentRequestState,
    userProjectLookupList,
  } = useSelector(selectLookupData)
  const dispatch = useDispatch()
  const pageKey = 'PAYMENTREQUEST'

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(paymentRequestGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(projectLookup())
    dispatch(basicLookup('PaymentRequestType'))
    dispatch(basicLookup('PaymentRequestState'))
    dispatch(userProjectLookup())
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const Columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'PAYMENTREQUEST.SEARCH.PROJECTNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'projectName',
        Cell: ({...props}) => <CustomCellDouble item={props} double='name' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'PAYMENTREQUEST.SEARCH.COMPANYTITLE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'companyTitle',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'PAYMENTREQUEST.SEARCH.PAYMENTREQUESTTYPENAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'paymentRequestTypeName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'PAYMENTREQUEST.SEARCH.PAYMENTREQUESTSTATENAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'paymentRequestStateName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'PAYMENTREQUEST.SEARCH.REQUESTEDPAYMENT'})}
            className='min-w-125px'
          />
        ),
        accessor: 'requestedPayment',
        Cell: ({...props}) => <CustomCell item={props} price />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'PAYMENTREQUEST.SEARCH.APPROVEDPAYMENT'})}
            className='min-w-125px'
          />
        ),
        accessor: 'approvedPayment',
        Cell: ({...props}) => <CustomCell item={props} price />,
      },
    ],
    [intl, handleSorting]
  )

  const actionButtons = useMemo(
    () => [
      {
        title: 'FILTER',
        icon: '/images/icons/duotune/general/gen031.svg',
        variant: 'light-primary',
        list: [
          {
            title: 'PAYMENTREQUEST.SEARCH.PROJECTNAME',
            data: projectLookupList,
            name: 'projectId',
          },
          {
            title: 'PAYMENTREQUEST.SEARCH.COMPANYTITLE',
            data: companyLookup,
            name: 'companyId',
          },
          {
            title: 'PAYMENTREQUEST.SEARCH.REQUESTEDUSERID',
            data: userProjectLookupList,
            name: 'requestedUserId',
          },
          {
            title: 'PAYMENTREQUEST.SEARCH.BEGINDATE',
            type: 'date',
            name: 'beginDate',
          },
          {
            title: 'PAYMENTREQUEST.SEARCH.ENDDATE',
            type: 'date',
            name: 'endDate',
          },
          {
            title: 'PAYMENTREQUEST.SEARCH.PAYMENTREQUESTTYPENAME',
            data: PaymentRequestType,
            name: 'paymentRequestTypeId',
          },
          {
            title: 'PAYMENTREQUEST.SEARCH.PAYMENTREQUESTSTATENAME',
            data: PaymentRequestState,
            name: 'paymentRequestStateId',
          },
        ],
      },
    ],
    [
      projectLookupList,
      companyLookup,
      userProjectLookupList,
      PaymentRequestType,
      PaymentRequestState,
    ]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.PAYMENTREQUEST'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={pageKey}
        />
        <KTCardBody>
          {!paymentRequest && <ListLoading />}
          {paymentRequest && (
            <Table
              items={paymentRequest}
              cols={Columns}
              handlePagination={handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
